import { useEffect, useState } from 'react';
import { api, type Asset, type Source } from '../lib/api';
import { Thumb, displayName } from './AssetGrid';
import { writeDrag, type DragAsset } from '../lib/drag';
import { mmss } from '../lib/timeline';
import { SourceThumb } from './Record';

// ---------------------------------------------------------------------------
// The library, in the workspace, so there is somewhere to drag from.
//
// The library already holds promoted, ready assets with posters and durations.
// What it never had was somewhere to put one — a card's footage and bed were
// chosen from dropdowns, which is choosing by name what you should be choosing
// by sight. This is the same list the Assets view shows, filtered to what a
// track could take, each card draggable onto the timeline below it.
// ---------------------------------------------------------------------------

type Filter = 'all' | Asset['kind'] | 'takes';

/**
 * The sources that are their own footage: a camera take, a Spyglass
 * recording, a tape or a file uploaded under Sources. None of these has an
 * asset behind it, so the library never showed them and the only way onto a
 * scene was the row's own "add to the video" control. Here they are in the
 * tray with everything else, and a drop cites the source directly.
 */
export function trayTakes(sources: readonly Source[]): Source[] {
  return sources
    .filter((s) => s.mode === 'stored' && s.status === 'ready' && !s.asset_id)
    .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
}

export function AssetTray({ brandId, onDragState, episode, episodeSources }: {
  brandId: string | null;
  /** 093: the asset ids gathered for this episode; the tray shows them first. */
  episode?: Set<string>;
  /** The source ids gathered for this episode — its takes — shown with them. */
  episodeSources?: Set<string>;
  /** Told what kind is in flight, so the tracks can say yes or no before the drop. */
  onDragState: (kind: Asset['kind'] | null) => void;
}) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [takes, setTakes] = useState<Source[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let live = true;
    // READY only. One still promoting has no media key and no poster, and a
    // track would accept it and then render a panel in its place — the drop
    // that succeeds and does nothing.
    api.assets(brandId).then((all) => {
      if (live) setAssets(all.filter((a) => a.status === 'ready' && a.kind !== 'document'));
    }).catch(() => { if (live) setAssets([]); });
    // The takes, beside the assets. Sources are listed per brand the same way.
    api.sources(brandId).then((r) => { if (live) setTakes(trayTakes(r.sources)); }).catch(() => { if (live) setTakes([]); });
    return () => { live = false; };
  }, [brandId]);

  const anyGathered = (episode?.size ?? 0) + (episodeSources?.size ?? 0) > 0;
  const gathered = anyGathered ? assets.filter((a) => episode?.has(a.id)) : null;
  const gatheredTakes = anyGathered ? takes.filter((t) => episodeSources?.has(t.id)) : null;
  const [wide, setWide] = useState(false);
  const pool = gathered && !wide ? gathered : assets;
  const takePool = gatheredTakes && !wide ? gatheredTakes : takes;
  const shown = pool.filter((a) => filter === 'all' || a.kind === filter);
  const shownTakes = filter === 'all' || filter === 'takes' ? takePool : [];

  return (
    <section className="tray" data-tool="library">
      <div className="tray-hd">
        <button className="tray-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <h4>{gathered && !wide ? 'This episode' : 'Library'}</h4>
          <span className="chip">{pool.length + takePool.length} ready</span>
        </button>
        {open && gathered && (
          <button type="button" className="chip" onClick={() => setWide((w) => !w)} title="The episode's collection, or the whole library">
            {wide ? 'this episode' : 'whole library'}
          </button>
        )}
        {open && (
          <div className="tray-filters" role="tablist">
            {(['all', 'takes', 'image', 'video', 'audio'] as Filter[]).map((k) => (
              <button key={k} role="tab" aria-selected={filter === k}
                      className={`chip${filter === k ? ' on' : ''}`}
                      onClick={() => setFilter(k)}>{k}</button>
            ))}
          </div>
        )}
      </div>

      {open && (shown.length === 0 && shownTakes.length === 0 ? (
        <p className="hint">
          {filter === 'takes'
            ? 'No takes yet. Capture a camera under Sources, or record one on the Record screen, and it appears here.'
            : assets.length === 0 && takes.length === 0
              ? 'Nothing ready in the library yet. Upload footage, stills or audio under Assets, or capture a camera under Sources.'
              : `No ${filter} in the library.`}
        </p>
      ) : (
        <div className="tray-grid">
          {/* The takes first: the newest work, and the thing a creator who
              just left the camera rig is looking for. */}
          {shownTakes.map((t) => {
            const seconds = t.duration_ms ? t.duration_ms / 1000 : null;
            // A take is footage to the tracks — kind 'video', so the B-roll
            // lane lights and Sound refuses it — and a source to the drop.
            const payload: DragAsset = { id: t.id, kind: 'video', name: t.name, seconds, sourceId: t.id };
            const what = t.kind === 'camera' ? 'take' : t.kind === 'screen' ? 'screen take' : t.kind === 'tape' ? 'tape' : 'footage';
            return (
              <div
                key={`take-${t.id}`}
                className="tray-item is-take"
                draggable
                title="Drag onto a scene — it cuts to this take with its own sound"
                onDragStart={(e) => { writeDrag(e, payload); onDragState('video'); }}
                onDragEnd={() => onDragState(null)}
              >
                <SourceThumb source={t} width={96} />
                <b>{t.name}</b>
                <em>{what}{seconds !== null ? ` · ${mmss(seconds)}` : ''}</em>
              </div>
            );
          })}
          {shown.map((a) => {
            const seconds = a.duration_ms ? a.duration_ms / 1000 : null;
            const payload: DragAsset = { id: a.id, kind: a.kind, name: displayName(a), seconds };
            return (
              <div
                key={a.id}
                className={`tray-item is-${a.kind}`}
                draggable
                title={`Drag onto a scene${a.kind === 'audio' ? ' or the Sound track' : ''}`}
                onDragStart={(e) => { writeDrag(e, payload); onDragState(a.kind); }}
                onDragEnd={() => onDragState(null)}
              >
                <Thumb asset={a} size={96} />
                <b>{displayName(a)}</b>
                <em>
                  {a.kind === 'image' ? 'still' : a.kind}
                  {seconds !== null ? ` · ${mmss(seconds)}` : ''}
                </em>
              </div>
            );
          })}
        </div>
      ))}
    </section>
  );
}
